import { useState } from "react";
import { Send, CheckCircle2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", phone: "", email: "", vehicle: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "sending", msg: "" });
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      const res = await axios.post(`${API}/contact`, payload);
      if (res.data?.ok) {
        setStatus({ state: "success", msg: t("contact.formSuccess") });
        setForm({ name: "", phone: "", email: "", vehicle: "", message: "" });
      } else {
        setStatus({ state: "error", msg: t("contact.formError") });
      }
    } catch (err) {
      setStatus({ state: "error", msg: t("contact.formError") });
    }
  };

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 p-6 sm:p-8 backdrop-blur" data-testid="contact-form">
      <div className="mb-6">
        <div className="tech-label !text-brand-red mb-2">{t("contact.formTitle")}</div>
        <h3 className="font-display font-extrabold text-2xl tracking-tight chrome-text">
          {t("contact.formSubtitle")}
        </h3>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="tech-label !text-[10px] mb-1.5 block">{t("contact.formName")} *</label>
            <Input
              data-testid="cf-name"
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="rounded-none h-11 bg-black border-zinc-700 text-white placeholder:text-zinc-500"
              placeholder="John Smith"
            />
          </div>
          <div>
            <label className="tech-label !text-[10px] mb-1.5 block">{t("contact.formPhone")} *</label>
            <Input
              data-testid="cf-phone"
              required
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className="rounded-none h-11 bg-black border-zinc-700 text-white placeholder:text-zinc-500"
              placeholder="201-522-6155"
            />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="tech-label !text-[10px] mb-1.5 block">{t("contact.formEmail")}</label>
            <Input
              data-testid="cf-email"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className="rounded-none h-11 bg-black border-zinc-700 text-white placeholder:text-zinc-500"
              placeholder="you@email.com"
            />
          </div>
          <div>
            <label className="tech-label !text-[10px] mb-1.5 block">{t("contact.formVehicle")}</label>
            <Input
              data-testid="cf-vehicle"
              value={form.vehicle}
              onChange={(e) => set("vehicle", e.target.value)}
              className="rounded-none h-11 bg-black border-zinc-700 text-white placeholder:text-zinc-500"
              placeholder="2020 Kenworth T680"
            />
          </div>
        </div>
        <div>
          <label className="tech-label !text-[10px] mb-1.5 block">{t("contact.formMessage")} *</label>
          <Textarea
            data-testid="cf-message"
            required
            rows={4}
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            className="rounded-none bg-black border-zinc-700 text-white placeholder:text-zinc-500"
            placeholder="..."
          />
        </div>

        <button
          data-testid="cf-submit"
          type="submit"
          disabled={status.state === "sending"}
          className="w-full inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redHover text-white font-bold py-3.5 text-base tracking-wide transition-colors disabled:opacity-60"
        >
          <Send size={16} />
          {status.state === "sending" ? t("contact.formSending") : t("contact.formSubmit")}
        </button>

        {status.state === "success" && (
          <div className="flex items-start gap-2 p-3 border border-emerald-700/50 bg-emerald-900/20 text-emerald-300 text-sm" data-testid="cf-success">
            <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
            <span>{status.msg}</span>
          </div>
        )}
        {status.state === "error" && (
          <div className="flex items-start gap-2 p-3 border border-amber-700/50 bg-amber-900/20 text-amber-300 text-sm" data-testid="cf-error">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{status.msg}</span>
          </div>
        )}
      </form>
    </div>
  );
}
